import { useCallback, useEffect } from "react";
import { Settings } from "../settings";
import { COIN_JACKPOT_SYMBOL_ID, isCoinSymbol, isJackpotSymbol, getJackpotTier, isBonusSymbol, getSymbolZIndex, ZEUS_COLLECT_ID, ZEUS_DOUBLE_ID, ZEUS_MYSTERY_ID, cellZIndex, COIN_SYMBOL_ID } from "../SymbolData";
import { coinValueAt } from "../Games/Shared/coinValues";
import { anticipationWaitsFor } from "../Games/Shared/scatterAnticipation";
import { soundManager } from "../SoundManager";
import { SOUND_NAME } from "../sounds";
import { useReelRefs } from "../Contexts/ReelRefsContext";
import { FEATURES_CONFIG } from "../featuresConfig";
import store from "../State/Store/store";
import { isTurboLike, isSuperTurbo } from "../utils/speedHelpers";
import { isSpinHeld, onSpinHoldRelease } from "../utils/spinHold";
import { restLandedPictures } from "../Games/Shared/turboRest";
import * as PIXI from "pixi.js";
import { fireCoinToLogo } from "../Games/Shared/coinToLogoTrail";
import { addLogoPotPoint } from "../Games/Shared/logoPot";
import TickerTimeout from "../utils/TickerTimeout";
import { wearJackpotCoinSkin } from "../Games/Shared/jackpotCoinSkin";

// Reusable point — avoids heap allocation per Zeus symbol landing
const _tempPoint = new PIXI.Point();

// ── Super-turbo sound dedup ──────────────────────────────────────────────────
// All reels land in the same frame in super turbo, so coin/zeus landing sounds
// would stack N× for nothing. This Set tracks which sounds have already fired
// this frame; a single rAF callback clears it for the next frame.
const _frameSoundsPlayed = new Set();
let _frameSoundRAF = null;

function _playOncePerFrame(soundName) {
  if (_frameSoundsPlayed.has(soundName)) {
    return;
  }
  _frameSoundsPlayed.add(soundName);
  soundManager.play(soundName);
  if (!_frameSoundRAF) {
    _frameSoundRAF = requestAnimationFrame(() => {
      _frameSoundsPlayed.clear();
      _frameSoundRAF = null;
    });
  }
}

/**
 * Apply a cell's render priority on the flat overlay.
 *
 * The base-game overlay is one sortable container of 15 sibling cells, so a
 * cell's zIndex orders it against the whole board. Landing is the main place
 * this gets set, but every path that repaints a board without a spin (reload,
 * intro, free-spin exit) has to set it too — otherwise those cells keep the
 * default zIndex and the board silently falls back to left-to-right paint
 * order until the next spin.
 */
const applyOverlayZIndex = (overlayController, symbolIndex, symbolId) => {
  const overlaySymbolContainer =
    overlayController?.overlaySymbolRefs?.[symbolIndex];
  if (overlaySymbolContainer && !overlaySymbolContainer.destroyed) {
    // A plain coin also stacks by its value (the round's, as it shows it).
    const value =
      symbolId === COIN_SYMBOL_ID
        ? coinValueAt(store.getState().freeSpins?.currentRound, overlayController.reelIndex, symbolIndex)
        : 0;
    overlaySymbolContainer.zIndex = cellZIndex(
      getSymbolZIndex(symbolId),
      overlayController.reelIndex,
      symbolIndex,
      value ?? 0,
    );
  }
};

/**
 * Landing animations for the FREE SPIN board.
 *
 * A copy of useReelLandingAnimations from useLandingAnimations.js, pointed at
 * the free-spin ref arrays instead of the base ones. Copied rather than
 * parameterised on purpose: the base game's hook stays exactly as it is, and
 * the bonus game is free to change its landing behaviour without any risk to
 * the board the game already ships with.
 *
 * The two are line-for-line identical today apart from the two refs below. If
 * you fix a bug in one, look at the other.
 *
 * Turbo mode: plays idle-only animations (lightweight, seamless transition to
 * win). Normal/manual stop: plays the full landing→idle sequence.
 */
export const useFreeSpinBasicLandingAnimations = () => {
  const {
    // The only difference from the base hook: the bonus board's own arrays.
    // Aliased so the body below stays a line-for-line copy.
    freeSpinBasicReelRefs: reelRefs,
    freeSpinBasicOverlayReelRefs: overlayReelRefs,
    jackpotsControllerRef,
    potChargingAnimatorRef,
    potControllersRef,
    // Cleopatra's Fire: the coin → logo fire trail (see below).
    freeSpinsCoinTrailRef,
    freeSpinsGameViewControllerRef,
  } = useReelRefs();

  // ── The turbo rest, made SURE of (2026-10-29) ── every landed cell still on
  // its picture goes to its idle / static when the board is a rest board (not
  // held, no win, no trigger): run as each reel lands unheld (the reels before
  // it may have landed held) and when a hold is let go with the board already
  // down. See Games/Shared/turboRest.
  const restTurboBoard = useCallback(
    (skipReel = -1) => {
      if (FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED) return;
      const round = store.getState().freeSpins.currentRound;
      if (isSpinHeld() || round?.triggerWin || round?.win > 0) return;
      restLandedPictures({
        overlayControllers: (overlayReelRefs.current || []).map((r) =>
          r?.getOverlayReelController?.(),
        ),
        reelControllers: (reelRefs.current || []).map((r) =>
          r?.getReelController?.(),
        ),
        skipReel,
        prepareCell: (overlay, reel, row, symbolId) => {
          applyOverlayZIndex(overlay, row, symbolId);
          if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
            wearJackpotCoinSkin(overlay, store.getState().freeSpins.currentRound, reel, row);
          }
        },
      });
    },
    [overlayReelRefs, reelRefs],
  );

  // Let go with the board already down: it rests now.
  useEffect(
    () =>
      onSpinHoldRelease("freeSpins", () => {
        const st = store.getState();
        if (!st.freeSpins?.active || st.holdAndWin?.active) return;
        restTurboBoard();
      }),
    [restTurboBoard],
  );

  const triggerReelLandingAnimations = useCallback(
    (reelIndex, reelData, betOverride = null, skipRAF = false) => {
      const overlayReelRef = overlayReelRefs.current?.[reelIndex];
      const overlayController = overlayReelRef?.getOverlayReelController();
      const reelController = reelRefs.current?.[reelIndex]?.getReelController();

      if (!overlayController || !reelController || !reelData) {
        return;
      }

      // Turbo mode: idle-only animations (lightweight, seamless win transitions)
      const currentSpeed = store.getState().settings.autoPlaySpeed;
      const isTurbo = isTurboLike(currentSpeed);
      const isSuperTurboMode = isSuperTurbo(currentSpeed);

      const textureHideOps = [];
      // ── Turbo lands on the `static` spines (ported from Catch the Cod,
      // 2026-10-28) ── a single press (or the spin after a hold is let go) with
      // no win: each symbol on its held `static`, above the reel frame. A held
      // run spins straight on, and a win's presentation works on the pictures —
      // both keep the pictures. Once per reel landing.
      // const landsStatic =
      //   !isSpinHeld() && !(store.getState().freeSpins.currentRound?.win > 0);
      // No presentation at all, not only no PAY: the win controller presents on
      // `triggerWin` (useWinController), which a 0-pay trigger also raises —
      // the free-spins scatter line, the Hold & Win trigger coins, a retrigger.
      // Those keep the pictures too, so a static never meets a presentation.
      const presentedRound = store.getState().freeSpins.currentRound;
      const landsStatic =
        !isSpinHeld() && !presentedRound?.triggerWin && !(presentedRound?.win > 0);
      // Track highest jackpot tier landed this reel (to play bar animation once)
      // Only tracked when JACKPOTS feature is enabled
      let landedJackpotTier = FEATURES_CONFIG.JACKPOTS.ENABLED ? null : undefined;
      const tierOrder = FEATURES_CONFIG.JACKPOTS.ENABLED ? { mini: 1, major: 2, grand: 3 } : null;

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbolId = reelData[symbolIndex];

        if (symbolId === undefined) {
          continue;
        }

        // ── Render priority (M > D > C > everything) ──
        // The overlay is one flat container of 15 sibling cells, so a cell's
        // zIndex now orders it against the WHOLE board rather than just its
        // own reel. Set here, from the landed outcome, because this runs
        // before any spine is shown — the cell is already in the right layer
        // by the time its animation appears, with no mid-animation reshuffle.
        //
        // Deliberately above every branch below: the anticipation skip and the
        // turbo texture path both `continue`, and those cells still need a
        // correct low priority. Reassigning all 15 every landing is also what
        // makes this self-healing — an orb's high z can't outlive its round.
        applyOverlayZIndex(overlayController, symbolIndex, symbolId);

        // A jackpot coin's plate reacts as it lands — the base game's call
        // (useLandingAnimations). No-op until this room's spine has the
        // jackpot_<tier>_hit clips.
        if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
          // The jackpot coin wears its tier's skin before its spine plays.
          const skin = wearJackpotCoinSkin(
            overlayController,
            store.getState().freeSpins.currentRound,
            reelIndex,
            symbolIndex,
          );
          const tier = skin?.replace(/^Jp_/, "");
          if (tier) freeSpinsGameViewControllerRef?.current?.playJackpotHit?.(tier);
        }

        // Track highest jackpot tier landed on this reel for bar animation
        if (FEATURES_CONFIG.JACKPOTS.ENABLED && isJackpotSymbol(symbolId)) {
          const tier = getJackpotTier(symbolId);
          if (tier && (!landedJackpotTier || tierOrder[tier] > tierOrder[landedJackpotTier])) {
            landedJackpotTier = tier;
          }
        }

        // --- POT Charging: Fire particle trail for Zeus symbols ---
        // Placed BEFORE turbo skip — trails must fire in ALL spin modes (normal, turbo, autoplay).
        // Only needs overlay container position (pre-positioned), not a playing spine animation.
        if (FEATURES_CONFIG.POT_CHARGING.ENABLED && isBonusSymbol(symbolId)) {
          const animator = potChargingAnimatorRef?.current;
          const overlaySymbolContainer = overlayController?.overlaySymbolRefs?.[symbolIndex];

          if (animator && overlaySymbolContainer && !overlaySymbolContainer.destroyed) {
            const trailContainer = animator.trailContainer;
            if (trailContainer && !trailContainer.destroyed) {
              _tempPoint.set(0, 0);
              const globalPos = overlaySymbolContainer.toGlobal(_tempPoint);
              const localPos = trailContainer.toLocal(globalPos);
              animator.fireTrail(symbolId, localPos, {
                onArrival: (potType) => {

                  const ctrl = potControllersRef?.current?.[potType];
                  if (!ctrl) return;
                  const state = store.getState().game;
                  const fs = state.featureState;
                  const isTrigger =
                    (fs?.type === "bonus") &&
                    fs?.phase === "intro";
                  // Only the ACTUALLY triggered pot type(s) get playTrigger
                  const typeToChar = { double: "D", mystery: "M", collect: "C" };
                  const charKey = typeToChar[potType]; // "D", "M", or "C"
                  const thisTypeTriggered = isTrigger &&
                    fs?.data?.respinGameType?.includes(charKey);

                  if (thisTypeTriggered) {
                    ctrl.playTrigger();
                  } else {
                    // Read API target stage from Redux — grow pot on bolt impact
                    const apiStage = state.potProgress?.[charKey]?.stage;
                    if (apiStage !== undefined && apiStage > ctrl.stage) {
                      // Check if this is a real visual growth (different animation)
                      // Stages 0 and 1 both use animation 1 — no visual change
                      const effectiveNew = Math.max(1, apiStage);
                      const effectiveCurrent = Math.max(1, ctrl.stage);
                      if (effectiveNew > effectiveCurrent) {
                        // Different animation level — full collect→in→idle chain
                        ctrl.setStage(apiStage);
                      } else {
                        // Same animation level (e.g., 0→1) — sync stage, hit only
                        ctrl.stage = apiStage;
                        ctrl.playHit();
                      }
                    } else {
                      // Stage already matches — play hit flash only (additional bolts this spin)
                      ctrl.playHit();
                    }
                  }
                },
                duration: undefined, // use default from config
                reelIndex,
                symbolIndex,
              });
            }
          }
        }

        // --- The Pot: a landed coin throws its trail at the logo (GDD §5.1) ---
        // Cleopatra's Fire — the base hook's branch, on this room's layer and
        // logo. Before the turbo skip, so it fires at every speed. NON-BLOCKING:
        // nothing waits for it.
        if (FEATURES_CONFIG.LOGO_POT?.TRAILS_ENABLED && isCoinSymbol(symbolId)) {
          const scene = freeSpinsGameViewControllerRef?.current;
          const own = freeSpinsCoinTrailRef?.current;
          fireCoinToLogo({
            // The scene's layer under the logo (falls in behind it); the
            // room's own trail layer if the spine has no such slot.
            layer: scene?.coinTrailContainer
              ? { trailContainer: scene.coinTrailContainer, symbolHeight: own?.symbolHeight }
              : own,
            from: overlayController?.overlaySymbolRefs?.[symbolIndex],
            target: freeSpinsGameViewControllerRef?.current?.getLogoTarget?.(),
            // onArrival: () => freeSpinsGameViewControllerRef?.current?.playLogoHit?.(),
            // The hit, and a point toward the next fire stage (logoPot).
            onArrival: () => {
              freeSpinsGameViewControllerRef?.current?.playLogoHit?.();
              addLogoPotPoint();
            },
          });
        }

        // Anticipation mode: only play landing animations for bonus symbols (Zeus coins).
        // Non-bonus symbols stay dark under the global DimReelSet overlay — no overlay
        // animation needed. Zeus coins get full landing→idle so they appear bright above dim.
        if (FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED &&
            store.getState().game.currentRound?.anticipation === true &&
            !isBonusSymbol(symbolId)) {
          continue;
        }

        // Turbo with landing animations disabled: skip all spine work, show static textures
        // EXCEPTION: When tension is active this spin, scatter/trigger symbols get full landing→idle
        // so the player sees animated scatters during tension sequences
        let forceLanding = false;
        if (isTurbo && !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED) {
          const isInTensionMode = reelController.hasTensionThisSpin === true;
          const isTriggerSymbol = FEATURES_CONFIG.TENSION.ENABLED &&
            FEATURES_CONFIG.TENSION.TRIGGER_SYMBOL?.includes(symbolId);
          // Cleopatra's Fire: the anticipation chain's own trigger symbols
          // (coins / scatters with hope) get their spine in turbo too — their
          // wait clips need one (2026-10-07).
          // The free spin's round (freeSpins slice — as its jackpot skins).
          const waitsInChain = anticipationWaitsFor(
            store.getState().freeSpins.currentRound,
            symbolId,
          );

          // if (!isInTensionMode || !isTriggerSymbol) {
          if ((!isInTensionMode || !isTriggerSymbol) && !waitsInChain) {
            // if (reelController.showSymbolTexture) {
            //   reelController.showSymbolTexture(symbolIndex);
            // }
            // The held `static` (see landsStatic) — its z-order is already set
            // above (applyOverlayZIndex), like every landed spine's.
            // if (landsStatic && overlayController.hasClip?.(symbolId, "static")) {
            //   overlayController.showIdleAnimation(symbolIndex, symbolId, betOverride, true, "static");
            //   textureHideOps.push({ reelController, symbolIndex });
            // } else if (reelController.showSymbolTexture) {
            // ── Its IDLE when it has one, else the `static` (2026-10-28: "when
            // we land and not hold spacebar we play idle and static") ── no
            // landing clip either way (Zeus GDD §7.2: "IN animation for
            // symbols is disabled" in turbo); the idle runs at once, unfrozen.
            if (landsStatic && overlayController.hasClip?.(symbolId, "idle")) {
              overlayController.showIdleAnimation(symbolIndex, symbolId, betOverride, false, "idle");
              textureHideOps.push({ reelController, symbolIndex });
            } else if (landsStatic && overlayController.hasClip?.(symbolId, "static")) {
              overlayController.showIdleAnimation(symbolIndex, symbolId, betOverride, true, "static");
              textureHideOps.push({ reelController, symbolIndex });
            } else if (reelController.showSymbolTexture) {
              reelController.showSymbolTexture(symbolIndex);
            }
            continue;
          } else {
            // Scatter during tension: force full landing→idle (not turbo idle-only)
            forceLanding = true;
          }
        }

        const { hasLanding, hasIdle } =
          overlayController.getAvailableAnimations(symbolIndex, symbolId);

        // Play landing sound based on symbol type
        // Super turbo: all reels land simultaneously — play each sound once per frame max
        if (isCoinSymbol(symbolId) && (hasLanding || hasIdle)) {
          if (isSuperTurboMode) {
            _playOncePerFrame(SOUND_NAME.c_in);
          } else {
            soundManager.play(SOUND_NAME.c_in);
          }
        } else if (isBonusSymbol(symbolId) && (hasLanding || hasIdle)) {
          if (isSuperTurboMode) {
            // Play one zeus landing sound for all zeus symbols this frame
            const zeusSound = symbolId === ZEUS_COLLECT_ID ? SOUND_NAME.cc_in
              : symbolId === ZEUS_DOUBLE_ID ? SOUND_NAME.cd_in
              : SOUND_NAME.cm_in;
            _playOncePerFrame(zeusSound);
          } else {
            if (symbolId === ZEUS_COLLECT_ID) soundManager.play(SOUND_NAME.cc_in);
            else if (symbolId === ZEUS_DOUBLE_ID) soundManager.play(SOUND_NAME.cd_in);
            else if (symbolId === ZEUS_MYSTERY_ID) soundManager.play(SOUND_NAME.cm_in);
          }
        }

        if (isTurbo && !forceLanding) {
          // Turbo: idle-only (skips landing animation entirely)
          if (hasIdle) {
            overlayController.showIdleAnimation(
              symbolIndex,
              symbolId,
              betOverride,
            );
            textureHideOps.push({ reelController, symbolIndex });
          } else {
            if (reelController.showSymbolTexture) {
              reelController.showSymbolTexture(symbolIndex);
            }
          }
        } else if (hasLanding || hasIdle) {
          // Round-level idle: show frozen overlay during landing so the swap from
          // texture→spine happens during the reel bounce (invisible to the user).
          // The idle animation stays frozen until all reels stop.
          if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE && !hasLanding) {
            if (hasIdle) {
              overlayController.showIdleAnimation(
                symbolIndex,
                symbolId,
                betOverride,
                true, // frozen — will be unfrozen when all reels stop
              );
              textureHideOps.push({ reelController, symbolIndex });
            }
          } else {

            // Normal: full landing→idle sequence (or landing→hold in round-level mode)
            overlayController.showLandingAnimation(
              symbolIndex,
              symbolId,
              betOverride,
            );
            textureHideOps.push({ reelController, symbolIndex });
          }
        } else {
          if (reelController.showSymbolTexture) {
            reelController.showSymbolTexture(symbolIndex);
          }
        }
      }

      // Fire jackpot bar highlight animation for highest tier landed this reel
      if (FEATURES_CONFIG.JACKPOTS.ENABLED && landedJackpotTier && jackpotsControllerRef?.current) {
        const ctrl = jackpotsControllerRef.current;
        if (landedJackpotTier === "mini") ctrl.playMini();
        else if (landedJackpotTier === "major") ctrl.playMajor();
        else if (landedJackpotTier === "grand") ctrl.playGrand();
      }

      // A reel landing UNHELD settles the reels that landed before it this
      // spin — a hold let go mid-landing left those on pictures (turboRest).
      if (
        isTurbo &&
        !FEATURES_CONFIG.SPINE_ANIMATIONS.TURBO_LANDING_ENABLED &&
        landsStatic
      ) {
        restTurboBoard(reelIndex);
      }

      // Deferred texture hide for landing animations only (need 1-frame overlap)
      if (!skipRAF && textureHideOps.length > 0) {
        requestAnimationFrame(() => {
          textureHideOps.forEach(({ reelController, symbolIndex }) => {
            if (reelController.hideSymbolTexture) {
              reelController.hideSymbolTexture(symbolIndex);
            }
          });
        });
      }

      return textureHideOps;
    },
    [
      overlayReelRefs,
      reelRefs,
      jackpotsControllerRef,
      potChargingAnimatorRef,
      potControllersRef,
      freeSpinsCoinTrailRef,
      freeSpinsGameViewControllerRef,
      restTurboBoard,
    ],
  );

  return { triggerReelLandingAnimations };
};

/**
 * The free-spins board's resting idles — the base game's "idle on load" pass
 * (useInitialIdleAnimations), pointed at the free-spins refs.
 *
 * Cleopatra's Fire: the room opens on the trigger board, and its symbols have
 * idles (Mad Labs' did not, so its entry left the static textures standing).
 * Called when the entry wipe lifts (TransitionDialog), so the board the player
 * sees is alive (Vladimir, 2026-09-29).
 */
export const useFreeSpinBasicInitialIdleAnimations = () => {
  const {
    freeSpinBasicReelRefs: reelRefs,
    freeSpinBasicOverlayReelRefs: overlayReelRefs,
  } = useReelRefs();

  // ── The board is built AT the midpoint, not before it ──
  //
  // BonusGameView mounts the free-spins reels and overlay only once
  // `freeSpins.active` is up, and TransitionDialog raises it in the same
  // midpoint handler that asks for these idles — so on that frame there are
  // no controllers yet and every cell was skipped. Ready = every reel and its
  // overlay exist, the overlay's spines are built and the reel holds its
  // board. Checked once per frame (the H&W room's whenReady), still under the
  // cover; given up after `frames` so a room that never builds cannot leak.
  const boardReady = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      const overlayController =
        overlayReelRefs.current?.[reelIndex]?.getOverlayReelController();
      const reelController = reelRefs.current?.[reelIndex]?.getReelController();
      if (!overlayController || !reelController) return false;
      if (!overlayController.prePositionedSpines?.size) return false;
      const first = reelController.symbolsContainer?.children?.[0];
      if ((first?.currentSymbolId ?? first?.symbolId) == null) return false;
    }
    return true;
  }, [overlayReelRefs, reelRefs]);

  const playIdles = useCallback(() => {
    for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
      const overlayController =
        overlayReelRefs.current?.[reelIndex]?.getOverlayReelController();
      const reelController = reelRefs.current?.[reelIndex]?.getReelController();
      if (!overlayController || !reelController) continue;

      for (
        let symbolIndex = 0;
        symbolIndex < Settings.SYMBOLS_VISIBLE;
        symbolIndex++
      ) {
        const symbol = reelController.symbolsContainer.children[symbolIndex];
        const symbolId = symbol?.currentSymbolId ?? symbol?.symbolId;
        if (symbolId === undefined || symbolId === null) continue;

        applyOverlayZIndex(overlayController, symbolIndex, symbolId);
        // A reloaded jackpot coin idles without ever landing: its skin here.
        if (symbolId === COIN_JACKPOT_SYMBOL_ID) {
          wearJackpotCoinSkin(
            overlayController,
            store.getState().freeSpins.currentRound,
            reelIndex,
            symbolIndex,
          );
        }

        // A settled Golden cell answers hasIdle false (see the overlay's
        // getAvailableAnimations) and keeps its texture.
        const { hasIdle } = overlayController.getAvailableAnimations(
          symbolIndex,
          symbolId,
        );
        if (!hasIdle) continue;

        overlayController.showIdleAnimation(symbolIndex, symbolId);
        // The texture goes a frame later, as on the base board — the spine's
        // textures upload first, and the overlap shows the same picture.
        new TickerTimeout(() => {
          reelController.hideSymbolTexture?.(symbolIndex);
        }, 0);
      }
    }
  }, [overlayReelRefs, reelRefs]);

  const triggerFreeSpinBasicIdleAnimations = useCallback((frames = 120) => {
    let raf = null;
    const tick = () => {
      raf = null;
      if (boardReady()) playIdles();
      else if (frames-- > 0) raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [boardReady, playIdles]);

  return { triggerFreeSpinBasicIdleAnimations };
};
